import { isEqual } from 'lodash-es';

import {
  isFlexibleTemplateType,
  type DocumentSectionSpec,
  type DocumentTemplate,
  type SectionOption,
} from '@/entities/templates';

import { findAllowedValue, isAllowedValue } from './blankValues';
import {
  EMPTY_LINE_PRESETS,
  hasLinePresets,
  type BlockPresets,
  type LinePresets,
  type NotePresets,
} from './blockPresets';
import { getTemplateDefaultPresets } from './presetTemplate';

// Seconds from the start of the recording, as in transcript chunk timecodes.
export type FillCitation = { start: number; end: number };

export type AiAnswer = { value: string; citation?: FillCitation };

export type TemplateDraft = {
  template: DocumentTemplate;
  // The clinician's answers. An empty string is an explicit clear that AI must not refill.
  answers: Record<string, string>;
  aiAnswers: Record<string, AiAnswer>;
  // Slots whose strict values may be combined, keyed like answers. The template
  // contract has no such flag (the generation prompt infers it from
  // instructions), so it lives in the working copy only.
  multiSelect?: Record<string, true>;
  // A block template's values set before the recording, by block id.
  blockPresets?: BlockPresets;
  // A block template's line operations set before the recording.
  linePresets?: LinePresets;
};

export const getDraftNotePresets = (draft: TemplateDraft): NotePresets => ({
  blocks: draft.blockPresets ?? {},
  lines: draft.linePresets ?? EMPTY_LINE_PRESETS,
});

export const setDraftNotePresets = (
  draft: TemplateDraft,
  { blocks, lines }: NotePresets,
): TemplateDraft => ({ ...draft, blockPresets: blocks, linePresets: lines });

export type DraftAnswers = Pick<TemplateDraft, 'answers' | 'aiAnswers'>;

export type ResolvedAnswer = {
  value: string;
  source: 'manual' | 'ai' | null;
  citation?: FillCitation;
};

// A labeled field is "Label: {{slot}}": a name followed by one value slot.
// The separator is what the template wrote between them (": ", " - ", " "),
// so a rename keeps the clinical sentence as it was.
// A statement has no slots and is either included or not.
// Any other text with slots is a phrase whose slots are filled in place,
// including a bare "{{slot}}" that has no name.
export type TemplateField =
  | { kind: 'labeled'; label: string; slot: string; separator: string }
  | { kind: 'statement' }
  | { kind: 'phrase'; slots: string[] };

const SLOT_PATTERN = /\{\{([\p{L}\p{N}_]+)\}\}/gu;
const LABELED_PATTERN =
  /^\s*([^{}]*?)(\s*[:\-–—]?\s*)\{\{([\p{L}\p{N}_]+)\}\}\s*$/u;

// Answers are keyed as scribe keys them (DocumentAnswerKey): the section
// code, the option index, -1 for the text or note of the section itself, and
// the slot. Keys are built and read only here.
export type AnswerKeyParts = {
  sectionCode: string;
  optionIndex: number;
  slot: string;
};

export const answerKey = (sectionCode: string, optionIndex = -1, slot = '') =>
  JSON.stringify([sectionCode, optionIndex, slot]);

export const parseAnswerKey = (key: string): AnswerKeyParts => {
  const [sectionCode, optionIndex, slot] = JSON.parse(key) as [
    string,
    number,
    string,
  ];
  return { sectionCode, optionIndex, slot };
};

// Where an answer goes after a structure edit; null drops it.
type AnswerKeyMove = (key: AnswerKeyParts) => AnswerKeyParts | null;

export const moveAnswerKeys = <T>(
  answers: Record<string, T>,
  move: AnswerKeyMove,
) =>
  Object.fromEntries(
    Object.entries(answers).flatMap(([key, value]) => {
      const next = move(parseAnswerKey(key));
      return next
        ? [[answerKey(next.sectionCode, next.optionIndex, next.slot), value]]
        : [];
    }),
  );

// Without stable field ids, a field of one structure is found in another by
// its text: the n-th field with a text is the n-th field with that text.
const matchFieldsByText = (from: SectionOption[], to: SectionOption[]) =>
  from.map((option, index) => {
    const occurrence = from
      .slice(0, index)
      .filter((item) => item.Text === option.Text).length;
    const matches = to.flatMap((item, toIndex) =>
      item.Text === option.Text ? [toIndex] : [],
    );
    return matches[occurrence] ?? null;
  });

export const followFieldsByText = (
  from: DocumentTemplate,
  to: DocumentTemplate,
): AnswerKeyMove => {
  const indexes = new Map(
    from.sections.map((section) => [
      section.Code,
      matchFieldsByText(
        section.Options,
        to.sections.find((item) => item.Code === section.Code)?.Options ?? [],
      ),
    ]),
  );
  return (key) => {
    if (key.optionIndex < 0) {
      return key;
    }
    const optionIndex = indexes.get(key.sectionCode)?.[key.optionIndex];
    return optionIndex === undefined || optionIndex === null
      ? null
      : { ...key, optionIndex };
  };
};

const getTextSlots = (text: string) => [
  ...new Set([...text.matchAll(SLOT_PATTERN)].map(([, slot]) => slot)),
];

// A placeholder without a `Slots` entry is a free slot, as the server reads
// it; entries come in the order of the text.
export const getOptionSlots = (option: SectionOption) => [
  ...new Set([...getTextSlots(option.Text), ...Object.keys(option.Slots)]),
];

// `Slots` entries the text does not name. Scribe never prints such an
// option, answered or not, and refuses to save a template that has one.
export const getOrphanSlots = (option: SectionOption) => {
  const named = getTextSlots(option.Text);
  return Object.keys(option.Slots).filter((slot) => !named.includes(slot));
};

// The strict values of a slot, empty for a free slot or one without an entry.
export const getSlotValues = (option: SectionOption, slot: string) =>
  option.Slots[slot]?.Values ?? [];

// Undefined when the option has no such slot.
export const getAllowedValues = (option: SectionOption, slot: string) =>
  getOptionSlots(option).includes(slot)
    ? getSlotValues(option, slot)
    : undefined;

export const resolveAnswer = (
  { answers, aiAnswers }: DraftAnswers,
  key: string,
): ResolvedAnswer => {
  if (answers[key] !== undefined) {
    return { value: answers[key], source: 'manual' };
  }
  const answer = aiAnswers[key];
  return answer
    ? { value: answer.value, source: 'ai', citation: answer.citation }
    : { value: '', source: null };
};

// A flexible section with fields takes a narrative note next to them.
export const hasSectionNote = (section: DocumentSectionSpec) =>
  section.Options.length > 0 && isFlexibleTemplateType(section.TemplateType);

export const getEffectiveAnswers = ({
  answers,
  aiAnswers,
}: DraftAnswers): Record<string, string> => ({
  ...Object.fromEntries(
    Object.entries(aiAnswers).map(([key, answer]) => [key, answer.value]),
  ),
  ...answers,
});

export const describeField = (option: SectionOption): TemplateField => {
  const slots = getOptionSlots(option);
  if (slots.length === 0) {
    return { kind: 'statement' };
  }
  const labeled = option.Text.match(LABELED_PATTERN);
  if (!labeled?.[1].trim() || slots.length !== 1 || slots[0] !== labeled[3]) {
    return { kind: 'phrase', slots };
  }
  return {
    kind: 'labeled',
    label: labeled[1],
    slot: labeled[3],
    separator: labeled[2],
  };
};

export const formatSlotName = (slot: string) =>
  slot
    .replace(/^__ai_/u, '')
    .replaceAll('_', ' ')
    .trim() || slot;

export type PhrasePart = { slot: string } | { text: string };

export const splitPhrase = (text: string): PhrasePart[] =>
  text
    .split(SLOT_PATTERN)
    .map((part, index) => (index % 2 === 1 ? { slot: part } : { text: part }));

// Several selected values of one slot are kept in a single answer string.
const VALUE_SEPARATOR = '\u001f';

export const splitAnswerValues = (value = '') =>
  value.split(VALUE_SEPARATOR).filter((item) => item.trim());

export const joinAnswerValues = (values: string[]) =>
  values.join(VALUE_SEPARATOR);

// What scribe's protocol render (joinProtocolValues) prints before each of
// several values of one slot: nothing before the first, ", " before every
// other one in every language, never a conjunction before the last. Values
// are never changed, even one that holds a conjunction of its own.
export const listProtocolValueSeparators = (count: number) =>
  Array.from({ length: count }, (_, index) => (index ? ', ' : ''));

export const joinProtocolValues = (values: string[]) => values.join(', ');

export const formatAnswerValue = (value: string) =>
  joinProtocolValues(splitAnswerValues(value));

// The stored value chosen for an allowed value: the value itself or, for a
// value with blanks, its filled form.
export const findChosenValue = (
  value: string,
  allowed: string[],
  item: string,
) =>
  splitAnswerValues(value).find(
    (chosen) => findAllowedValue(allowed, chosen) === item,
  );

export const toggleAnswerValue = (
  value: string,
  item: string,
  allowed: string[] = [item],
) => {
  const values = splitAnswerValues(value);
  const chosen = findChosenValue(value, allowed, item);
  return joinAnswerValues(
    chosen === undefined
      ? [...values, item]
      : values.filter((current) => current !== chosen),
  );
};

export const replaceAnswerValue = ({
  value,
  allowed,
  item,
  next,
}: {
  value: string;
  allowed: string[];
  item: string;
  next: string;
}) => {
  const values = splitAnswerValues(value);
  const chosen = findChosenValue(value, allowed, item);
  return joinAnswerValues(
    chosen === undefined
      ? [...values, next]
      : values.map((current) => (current === chosen ? next : current)),
  );
};

const isSlotAnswered = (
  option: SectionOption,
  slot: string,
  value: string | undefined,
) => {
  const allowed = getSlotValues(option, slot);
  const values = splitAnswerValues(value);
  return (
    values.length > 0 &&
    (!allowed.length || values.every((item) => isAllowedValue(allowed, item)))
  );
};

const isFieldFilled = (
  sectionCode: string,
  optionIndex: number,
  option: SectionOption,
  answers: TemplateDraft['answers'],
) => {
  const slots = getOptionSlots(option);
  if (slots.length === 0) {
    return !!answers[answerKey(sectionCode, optionIndex)];
  }
  return slots.every((slot) =>
    isSlotAnswered(
      option,
      slot,
      answers[answerKey(sectionCode, optionIndex, slot)],
    ),
  );
};

// A section counts once: it is answered when any of its values, statements,
// its text or its note is. Values are recorded only when dictated, so the
// alternatives of a section are never all answered and a count per value
// could not reach its total.
export const isSectionAnswered = (
  section: DocumentSectionSpec,
  answers: TemplateDraft['answers'],
) =>
  ((!section.Options.length || hasSectionNote(section)) &&
    !!answers[answerKey(section.Code)]?.trim()) ||
  section.Options.some((option, index) => {
    const slots = getOptionSlots(option);
    return slots.length
      ? slots.some((slot) =>
          isSlotAnswered(
            option,
            slot,
            answers[answerKey(section.Code, index, slot)],
          ),
        )
      : !!answers[answerKey(section.Code, index)];
  });

type TemplateProgress = {
  filled: number;
  total: number;
  optionalFilled: number;
  optionalTotal: number;
};

// Required sections are the progress; optional ones are counted apart, as
// leaving them empty is expected.
export const getTemplateProgress = (draft: TemplateDraft): TemplateProgress => {
  const answers = getEffectiveAnswers(draft);
  return draft.template.sections.reduce(
    (progress, section) => {
      const filled = isSectionAnswered(section, answers) ? 1 : 0;
      return section.Required
        ? {
            ...progress,
            filled: progress.filled + filled,
            total: progress.total + 1,
          }
        : {
            ...progress,
            optionalFilled: progress.optionalFilled + filled,
            optionalTotal: progress.optionalTotal + 1,
          };
    },
    { filled: 0, total: 0, optionalFilled: 0, optionalTotal: 0 },
  );
};

export const getMissingRequiredSections = (
  draft: DraftAnswers & Pick<TemplateDraft, 'template'>,
) => {
  const answers = getEffectiveAnswers(draft);
  return draft.template.sections.filter(
    (section) => section.Required && !isSectionAnswered(section, answers),
  );
};

export const getDraftSectionText = (
  section: DocumentSectionSpec,
  answers: TemplateDraft['answers'],
) => {
  if (!section.Options.length) {
    return answers[answerKey(section.Code)] ?? '';
  }
  return section.Options.flatMap((option, index) => {
    if (
      !isFieldFilled(section.Code, index, option, answers) ||
      getOrphanSlots(option).length
    ) {
      return [];
    }
    const slots = getOptionSlots(option);
    if (slots.length === 0) {
      return [option.Text];
    }
    return [
      slots.reduce(
        (text, slot) =>
          text.replaceAll(
            `{{${slot}}}`,
            formatAnswerValue(
              answers[answerKey(section.Code, index, slot)] ?? '',
            ),
          ),
        option.Text,
      ),
    ];
  }).join('\n');
};

// A pristine working copy can be replaced without asking: nothing would be lost.
export const isDraftPristine = (
  draft: TemplateDraft,
  source: DocumentTemplate,
) =>
  Object.values(getEffectiveAnswers(draft)).every((value) => !value.trim()) &&
  !Object.keys(draft.multiSelect ?? {}).length &&
  isEqual(draft.blockPresets ?? {}, getTemplateDefaultPresets(source)) &&
  !hasLinePresets(draft.linePresets ?? EMPTY_LINE_PRESETS) &&
  JSON.stringify(draft.template) === JSON.stringify(source);

export const copyTemplateForEncounter = (
  template: DocumentTemplate,
): TemplateDraft => ({
  template: structuredClone(template),
  answers: {},
  aiAnswers: {},
  multiSelect: {},
});

export type WorkingCopy = { source: DocumentTemplate; draft: TemplateDraft };

// The values chosen before the start begin as the version's defaults.
export const createWorkingCopy = (source: DocumentTemplate): WorkingCopy => ({
  source,
  draft: {
    ...copyTemplateForEncounter(source),
    blockPresets: getTemplateDefaultPresets(source),
  },
});

// Values and multi-select belong to the encounter; only sections, fields,
// slots and instructions are a change of the template itself.
export const isStructureChanged = ({ draft, source }: WorkingCopy) =>
  JSON.stringify(draft.template) !== JSON.stringify(source);

// Slot names are technical keys in ASCII snake_case whatever the template
// language. Russian follows the simplified BGN/PCGN romanization, with the
// other Cyrillic letters of Ukrainian and Kazakh.
const CYRILLIC_TO_LATIN: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'kh',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'shch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
  є: 'ye',
  і: 'i',
  ї: 'yi',
  ґ: 'g',
  ў: 'u',
  ә: 'a',
  ғ: 'gh',
  қ: 'q',
  ң: 'ng',
  ө: 'o',
  ұ: 'u',
  ү: 'u',
  һ: 'h',
};

// Latin letters that do not decompose into a base letter and a diacritic.
const LATIN_LETTERS: Record<string, string> = {
  ß: 'ss',
  æ: 'ae',
  œ: 'oe',
  ø: 'o',
  ł: 'l',
  đ: 'd',
  ð: 'd',
  þ: 'th',
  ı: 'i',
};

// Cyrillic is replaced before decomposing, which would turn "й" into "и".
const toSlotId = (name: string) =>
  [...name.normalize('NFC').toLowerCase()]
    .map((char) => CYRILLIC_TO_LATIN[char] ?? LATIN_LETTERS[char] ?? char)
    .join('')
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .replace(/[^a-z0-9]+/gu, '_')
    .replace(/^_|_$/gu, '');

const withFreeSlotSuffix = (base: string, taken: string[]) => {
  if (!taken.includes(base)) {
    return base;
  }
  const suffix = Array.from(
    { length: taken.length + 1 },
    (_, index) => index + 2,
  ).find((number) => !taken.includes(`${base}_${number}`));
  return `${base}_${suffix}`;
};

export const createSlotId = (name: string, taken: string[]) => {
  const base = toSlotId(name);
  return base ? withFreeSlotSuffix(base, taken) : base;
};
